// Blog post summaries: the frontmatter of each post in content/blog (schema in source.config.ts).
// The pages, the landing page and the sitemap read them through src/server/blog/BlogIndex.ts.

/**
 * The subjects a cover can illustrate (BlogCover): a post's `cover` in its frontmatter, and the
 * landing page's feature cards.
 */
export const BLOG_COVERS = [
  "integration-paths",
  "mandatory-rules",
  "jurisdiction-rules",
  "media-component",
  "widgets",
  "sensitive-media",
  "accuracy",
  "delivery",
  "organisations",
  "server-upload",
  "age-checks",
  "hash-lists",
  "upload-presets",
  "comments",
  "profile",
  "direct-upload",

  "formats",
  "ingest",
  "notifications",
  "events",
  "uploads",
  "lenses",
  "policies",
  "webhooks",
  "sdks",
  "search",
  "moderation",
  "access",
  "ocr",
  "media",
  "websites",
  "link-safety",
  "team",
  "categories",
  "reports",
  "organisation",
  "pagination",
  "lifecycle",
  "authentication",
  "testing",
  "errors",
  "appeals",
  "human-review",
  "local-development",
  "assistants",
  "people",
  "conversation",
] as const;
export type BlogCover = (typeof BLOG_COVERS)[number];

/** Where a post's Open Graph / X image is served (rendered by vite/blogSocialImages.ts). */
export const blogSocialImagePath = (slug: string) => `/social/blog/${slug}.png`;

/** The URL pages link a post's social image at: its path, versioned by what the images are drawn
 * from so social networks fetch them again after a change. */
export const getBlogSocialImageUrl = (slug: string) =>
  import.meta.env.PUBLIC_BLOG_SOCIAL_IMAGE_VERSION
    ? `${blogSocialImagePath(slug)}?${import.meta.env.PUBLIC_BLOG_SOCIAL_IMAGE_VERSION}`
    : blogSocialImagePath(slug);

/** A post's frontmatter and slug (`content/blog/<slug>.mdx` is `/blog/<slug>`). */
export interface BlogPostSummary {
  slug: string;
  title: string;
  description: string;
  /** Publication date, `YYYY-MM-DD`. */
  date: string;
  cover: BlogCover;
  readTime: string;
  tags: string[];
}

export type BlogPostFrontmatter = Omit<BlogPostSummary, "slug"> & {
  /** Unpublished: left out unless `includeDrafts` is set (only in `vite dev`). */
  draft?: boolean;
  /** The SDKs a post is about: it is unpublished, like a draft, until every one of them is
   * (src/lib/SdkConstants.ts). */
  sdks?: string[];
  /** The feature switches a post is about (`*Constants.ts`, such as `ENABLE_LINK_SAFETY`): it is
   * unpublished, like a draft, until every one of them is on. */
  switches?: string[];
};

/**
 * Whether a post is published: not a draft, every SDK it is about is published and every switch
 * it names is on. A switch nobody reports as on keeps the post unpublished.
 */
export const isBlogPostPublished = (
  frontmatter: Pick<BlogPostFrontmatter, "draft" | "sdks" | "switches">,
  isSdkPublished: (sdk: string) => boolean,
  isSwitchOn: (name: string) => boolean = () => false,
) =>
  !frontmatter.draft &&
  (frontmatter.sdks ?? []).every(isSdkPublished) &&
  (frontmatter.switches ?? []).every(isSwitchOn);

/** `…/content/blog/lenses.mdx` → `lenses`. */
export const getBlogPostSlug = (file: string) => file.replace(/^.*\//, "").replace(/\.mdx?$/, "");

/** Summaries by file, newest first (ties by slug), as the blog index lists them. Unpublished posts
 * (`isBlogPostPublished`) are left out unless `includeDrafts` is set. */
export const toBlogPostSummaries = (
  frontmatterByFile: Record<string, BlogPostFrontmatter>,
  {
    includeDrafts = false,
    isSdkPublished,
    isSwitchOn,
  }: {
    includeDrafts?: boolean;
    isSdkPublished: (sdk: string) => boolean;
    isSwitchOn?: (name: string) => boolean;
  },
): BlogPostSummary[] =>
  Object.entries(frontmatterByFile)
    .filter(
      ([, frontmatter]) =>
        includeDrafts || isBlogPostPublished(frontmatter, isSdkPublished, isSwitchOn),
    )
    .map(([file, frontmatter]) => ({
      slug: getBlogPostSlug(file),
      title: frontmatter.title,
      description: frontmatter.description,
      date: frontmatter.date,
      cover: frontmatter.cover,
      readTime: frontmatter.readTime,
      tags: frontmatter.tags,
    }))
    .sort(
      (left, right) => right.date.localeCompare(left.date) || left.slug.localeCompare(right.slug),
    );
